import { useCallback } from 'react';

/**
 * Custom hook for e-commerce tracking via GTM dataLayer
 */
export const useEcommerceTracking = () => {
  // Track when a product is viewed
  const trackViewItem = useCallback((product) => {
    if (!window.dataLayer || !product) return;

    window.dataLayer.push({
      event: 'view_item',
      ecommerce: {
        items: [{
          item_id: product.sku || product.id,
          item_name: product.name,
          item_category: product.categories?.[0]?.name || 'Uncategorized',
          price: parseFloat(product.sale_price || product.regular_price || 0),
          quantity: 1
        }]
      }
    });
  }, []);

  // Track when a product is added to cart
  const trackAddToCart = useCallback((product, quantity = 1) => {
    if (!window.dataLayer || !product) return;

    const price = parseFloat(product.sale_price || product.regular_price || 0);

    window.dataLayer.push({
      event: 'add_to_cart',
      ecommerce: {
        currency: 'USD',
        value: price * quantity,
        items: [{
          item_id: product.sku || product.id,
          item_name: product.name,
          item_category: product.categories?.[0]?.name || 'Uncategorized',
          price: price,
          quantity: quantity
        }]
      }
    });
  }, []);

  // Track when a product is removed from cart
  const trackRemoveFromCart = useCallback((product, quantity = 1) => {
    if (!window.dataLayer || !product) return;

    const price = parseFloat(product.sale_price || product.regular_price || 0);

    window.dataLayer.push({
      event: 'remove_from_cart',
      ecommerce: {
        currency: 'USD',
        value: price * quantity,
        items: [{
          item_id: product.sku || product.id,
          item_name: product.name,
          price: price,
          quantity: quantity
        }]
      }
    });
  }, []);

  // Track when cart is viewed
  const trackViewCart = useCallback((cartItems) => {
    if (!window.dataLayer || !cartItems || cartItems.length === 0) return;

    const items = cartItems.map(item => ({
      item_id: item.sku || item.id,
      item_name: item.name,
      item_category: item.categories?.[0]?.name || 'Uncategorized',
      price: parseFloat(item.sale_price || item.regular_price || 0),
      quantity: item.quantity || 1
    }));

    const totalValue = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    window.dataLayer.push({
      event: 'view_cart',
      ecommerce: {
        currency: 'USD',
        value: totalValue,
        items: items
      }
    });
  }, []);

  // Track when checkout begins
  const trackBeginCheckout = useCallback((cartItems) => {
    if (!window.dataLayer || !cartItems || cartItems.length === 0) return;

    const items = cartItems.map(item => ({
      item_id: item.sku || item.id,
      item_name: item.name,
      item_category: item.categories?.[0]?.name || 'Uncategorized',
      price: parseFloat(item.sale_price || item.regular_price || 0),
      quantity: item.quantity || 1
    }));

    const totalValue = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    window.dataLayer.push({
      event: 'begin_checkout',
      ecommerce: {
        currency: 'USD',
        value: totalValue,
        items: items
      }
    });
  }, []);

  // Track when shipping info is added
  const trackAddShippingInfo = useCallback((cartItems, shippingTier) => {
    if (!window.dataLayer || !cartItems || cartItems.length === 0) return;

    const items = cartItems.map(item => ({
      item_id: item.sku || item.id,
      item_name: item.name,
      item_category: item.categories?.[0]?.name || 'Uncategorized',
      price: parseFloat(item.sale_price || item.regular_price || 0),
      quantity: item.quantity || 1
    }));

    const totalValue = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    window.dataLayer.push({
      event: 'add_shipping_info',
      ecommerce: {
        currency: 'USD',
        value: totalValue,
        shipping_tier: shippingTier || 'Standard',
        items: items
      }
    });
  }, []);

  // Track when payment info is added
  const trackAddPaymentInfo = useCallback((cartItems, paymentType) => {
    if (!window.dataLayer || !cartItems || cartItems.length === 0) return;

    const items = cartItems.map(item => ({
      item_id: item.sku || item.id,
      item_name: item.name,
      item_category: item.categories?.[0]?.name || 'Uncategorized',
      price: parseFloat(item.sale_price || item.regular_price || 0),
      quantity: item.quantity || 1
    }));

    const totalValue = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    window.dataLayer.push({
      event: 'add_payment_info',
      ecommerce: {
        currency: 'USD',
        value: totalValue,
        payment_type: paymentType || 'Credit Card',
        items: items
      }
    });
  }, []);

  // Track purchase completion
  const trackPurchase = useCallback((orderId, items, total, tax = 0, shipping = 0) => {
    if (!window.dataLayer || !items || items.length === 0) return;

    const ecommerceItems = items.map(item => ({
      item_id: item.sku || item.id,
      item_name: item.name,
      item_category: item.categories?.[0]?.name || 'Uncategorized',
      price: parseFloat(item.sale_price || item.regular_price || 0),
      quantity: item.quantity || 1
    }));

    window.dataLayer.push({
      event: 'purchase',
      ecommerce: {
        transaction_id: orderId,
        value: parseFloat(total),
        tax: parseFloat(tax),
        shipping: parseFloat(shipping),
        currency: 'USD',
        items: ecommerceItems
      }
    });
  }, []);

  return {
    trackViewItem,
    trackAddToCart,
    trackRemoveFromCart,
    trackViewCart,
    trackBeginCheckout,
    trackAddShippingInfo,
    trackAddPaymentInfo,
    trackPurchase
  };
};

export default useEcommerceTracking;
